import { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2, Save, Trash2 } from "lucide-react";

const SUGGESTIONS = [
  "TWILIO_ACCOUNT_SID",
  "TWILIO_AUTH_TOKEN",
  "TWILIO_PHONE_NUMBER",
  "ALERT_PHONE_NUMBER",
  "PINECONE_API_KEY",
  "RESEND_API_KEY",
  "SENDER_EMAIL",
];

export const ApiKeyVault = () => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await adminApi.get("/admin/integrations/keys");
      setKeys(r.data || []);
    } catch (_e) {}
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const n = name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    if (!n || !value.trim()) {
      toast.error("Provide both a key name and its value.");
      return;
    }
    setSaving(true);
    try {
      await adminApi.put("/admin/integrations/keys", { name: n, value: value.trim() });
      toast.success(`${n} saved securely.`);
      setName("");
      setValue("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not save key.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    try {
      await adminApi.delete(`/admin/integrations/keys/${n}`);
      toast.success(`${n} removed.`);
      await load();
    } catch (_e) {
      toast.error("Could not remove key.");
    }
  };

  return (
    <section data-testid="api-key-vault" className="bg-white border border-[#E3DED5] p-6 lg:p-8 mt-8">
      <div className="flex items-center gap-3">
        <KeyRound size={18} className="text-[#B0895C]" />
        <h2 className="font-serif-display text-2xl text-[#2A1F1A]">API Depository</h2>
      </div>
      <p className="text-sm text-[#6E6E70] mt-2 max-w-2xl">
        Populate keys for Twilio, Pinecone, Resend and any service Sasha needs. Values are stored
        securely, shown masked, and read by the backend automatically — no redeploy needed.
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`keyvault-suggest-${s.toLowerCase()}`}
            onClick={() => setName(s)}
            className={`text-[11px] uppercase tracking-wide px-2.5 py-1.5 border transition-colors ${
              name === s ? "border-[#C03028] text-[#C03028]" : "border-[#E3DED5] text-[#6E6E70] hover:border-[#B0895C]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_1.4fr_auto] gap-3 items-end">
        <div>
          <Label className="eyebrow">Key name</Label>
          <Input
            data-testid="keyvault-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="PINECONE_API_KEY"
            className="editorial-input mt-2 font-mono text-sm uppercase"
          />
        </div>
        <div>
          <Label className="eyebrow">Value</Label>
          <Input
            data-testid="keyvault-value-input"
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Paste the key / token / value"
            className="editorial-input mt-2 font-mono text-sm"
          />
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="keyvault-save-btn"
          className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save key
        </button>
      </div>

      {keys.length > 0 && (
        <div className="mt-6 border border-[#E3DED5] divide-y divide-[#E3DED5]" data-testid="keyvault-list">
          {keys.map((k) => (
            <div key={k.name} className="flex items-center justify-between gap-4 px-4 py-3" data-testid={`keyvault-row-${k.name.toLowerCase()}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#2A1F1A] font-mono">{k.name}</div>
                <div className="text-xs text-[#6E6E70] font-mono truncate">{k.masked_value}</div>
              </div>
              <button
                type="button"
                onClick={() => remove(k.name)}
                data-testid={`keyvault-delete-${k.name.toLowerCase()}`}
                className="text-[#8E8F90] hover:text-[#C03028] transition-colors shrink-0"
                title={`Remove ${k.name}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 text-xs text-[#8E8F90]">
        Currently used by Sasha: <code className="bg-[#F2EFE9] px-1">RESEND_API_KEY</code> +{" "}
        <code className="bg-[#F2EFE9] px-1">SENDER_EMAIL</code> power the owner-letter emails.{" "}
        <code className="bg-[#F2EFE9] px-1">TWILIO_ACCOUNT_SID</code> +{" "}
        <code className="bg-[#F2EFE9] px-1">TWILIO_AUTH_TOKEN</code> +{" "}
        <code className="bg-[#F2EFE9] px-1">TWILIO_PHONE_NUMBER</code> +{" "}
        <code className="bg-[#F2EFE9] px-1">ALERT_PHONE_NUMBER</code> enable SMS alerts on every new
        host application, Your Sasha request and investing lead. Phone numbers in E.164 format (e.g. +19714608756).
      </p>
    </section>
  );
};
